<template>
  <NavBar pageName="Administrator - Import Veridian"></NavBar>
  <v-container style="margin-top: 185px;">
    <!-- Search IMSI Section -->
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-sim</v-icon>
            Search IMSI
          </v-card-title>
          <v-card-subtitle class="pb-0">
            Search by IMSI serial number to add to import list
          </v-card-subtitle>

          <v-card-text>
            <v-row align="center">
              <v-col cols="12" sm="9">
                <v-text-field
                  v-model="searchTerm"
                  label="IMSI serial or number (min 3 characters)"
                  density="compact"
                  variant="outlined"
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-magnify"
                  @keyup.enter="doSearch"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <v-btn
                  color="primary"
                  :loading="searching"
                  :disabled="!searchTerm || searchTerm.trim().length < 3"
                  @click="doSearch"
                  block
                >
                  <v-icon class="mr-1">mdi-magnify</v-icon>
                  Search
                </v-btn>
              </v-col>
            </v-row>

            <v-alert
              v-if="searchPerformed && !searching && searchResults.length === 0"
              type="info"
              variant="tonal"
              density="compact"
              class="mt-4"
            >
              No IMSI found matching "{{ lastSearchTerm }}"
            </v-alert>

            <v-data-table
              v-if="searchResults.length > 0"
              :headers="searchHeaders"
              :items="searchResults"
              class="mt-4 elevation-1"
              density="compact"
              :items-per-page="10"
            >
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  v-if="!isInImportList(item.serial)"
                  color="success"
                  size="small"
                  variant="tonal"
                  @click="addToImportList(item)"
                >
                  <v-icon class="mr-1">mdi-plus</v-icon>
                  Add
                </v-btn>
                <v-chip v-else color="grey" size="small">Added</v-chip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Import List Section -->
    <v-row class="mt-2">
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center justify-space-between">
            <div>
              <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
              Import List ({{ importList.length }})
            </div>
            <div>
              <v-btn
                v-if="importList.length > 0"
                color="grey"
                size="small"
                variant="text"
                class="mr-2"
                @click="clearImportList"
              >
                Clear All
              </v-btn>
              <v-btn
                color="primary"
                :loading="importing"
                :disabled="importList.length === 0"
                @click="doImport"
              >
                <v-icon class="mr-1">mdi-database-import</v-icon>
                Import IMSIs
              </v-btn>
            </div>
          </v-card-title>

          <v-card-text v-if="importList.length === 0">
            <v-alert type="info" variant="tonal" density="compact">
              No IMSIs in the import list. Use the search above to find and add IMSIs.
            </v-alert>
          </v-card-text>

          <v-card-text v-else>
            <v-data-table
              :headers="importListHeaders"
              :items="importList"
              density="compact"
              :items-per-page="-1"
              hide-default-footer
            >
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  color="error"
                  size="x-small"
                  variant="text"
                  icon="mdi-close"
                  @click="removeFromImportList(item.serial)"
                ></v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Import Results Section -->
    <v-row v-if="importResults.length > 0" class="mt-2">
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-clipboard-check-outline</v-icon>
            Import Results
          </v-card-title>

          <v-card-text>
            <v-alert
              v-for="(result, idx) in importResults"
              :key="idx"
              :type="resultAlertType(result.status)"
              variant="tonal"
              density="compact"
              class="mb-2"
            >
              <strong>{{ result.serial }}</strong>: {{ result.message }}
              <div v-if="result.details && result.details.length > 0" class="mt-1">
                <v-chip
                  v-for="(d, i) in result.details"
                  :key="i"
                  size="x-small"
                  class="mr-1"
                  color="primary"
                  variant="outlined"
                >
                  {{ d.type }} - service #{{ d.service_id }} (CS #{{ d.cs_id }})
                </v-chip>
              </div>
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Error message -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script>
import NavBar from "@/components/NavBar.vue"
import { searchImsiForImport, importImsisFromVeridian } from "@/composables/useVeridianImportAPI"
import { useStore } from "vuex"
import { ref, computed } from "vue"

export default {
  name: "AdminVeridianImportView",
  components: { NavBar },
  setup() {
    const store = useStore()
    const token = computed(() => store.getters.getToken)

    const searchTerm = ref('')
    const searching = ref(false)
    const searchPerformed = ref(false)
    const lastSearchTerm = ref('')
    const searchResults = ref([])

    const importList = ref([])
    const importing = ref(false)
    const importResults = ref([])

    const snackbar = ref(false)
    const snackbarText = ref('')
    const snackbarColor = ref('success')

    const searchHeaders = [
      { title: 'Serial', key: 'serial', sortable: true },
      { title: 'IMSI', key: 'imsi', sortable: true },
      { title: 'Reseller', key: 'reseller_name', sortable: true },
      { title: 'Client', key: 'allocated_to_client', sortable: true },
      { title: '', key: 'actions', sortable: false, align: 'end' }
    ]

    const importListHeaders = [
      { title: '#', key: 'index', sortable: false, width: '50px' },
      { title: 'Serial', key: 'serial', sortable: false },
      { title: 'IMSI', key: 'imsi', sortable: false },
      { title: 'Reseller', key: 'reseller_name', sortable: false },
      { title: 'Client', key: 'allocated_to_client', sortable: false },
      { title: '', key: 'actions', sortable: false, align: 'end', width: '50px' }
    ]

    const doSearch = async () => {
      if (!searchTerm.value || searchTerm.value.trim().length < 3) return

      searching.value = true
      searchPerformed.value = false
      searchResults.value = []

      const result = await searchImsiForImport(token.value, searchTerm.value.trim())

      if (result.error) {
        showSnackbar(result.message, 'error')
      } else {
        searchResults.value = result.imsis || []
      }

      lastSearchTerm.value = searchTerm.value.trim()
      searchPerformed.value = true
      searching.value = false
    }

    const isInImportList = (serial) => {
      return importList.value.some(item => item.serial === serial)
    }

    const addToImportList = (imsi) => {
      if (!isInImportList(imsi.serial)) {
        importList.value.push({
          ...imsi,
          index: importList.value.length + 1
        })
        showSnackbar(`Added ${imsi.serial} to import list`, 'success')
      }
    }

    const removeFromImportList = (serial) => {
      importList.value = importList.value.filter(item => item.serial !== serial)
      // Re-index
      importList.value.forEach((item, idx) => {
        item.index = idx + 1
      })
    }

    const clearImportList = () => {
      importList.value = []
      importResults.value = []
    }

    const doImport = async () => {
      if (importList.value.length === 0) return

      importing.value = true
      importResults.value = []

      const serials = importList.value.map(item => item.serial)
      const result = await importImsisFromVeridian(token.value, serials)

      if (result.error && !result.results) {
        showSnackbar(result.message, 'error')
      } else {
        importResults.value = result.results || []
        const successCount = importResults.value.filter(r => r.status === 'success').length
        const errorCount = importResults.value.filter(r => r.status === 'error').length
        const skippedCount = importResults.value.filter(r => r.status === 'skipped').length
        showSnackbar(
          `Import done: ${successCount} success, ${skippedCount} skipped, ${errorCount} errors`,
          errorCount > 0 ? 'warning' : 'success'
        )
      }

      importing.value = false
    }

    const resultAlertType = (status) => {
      switch (status) {
        case 'success': return 'success'
        case 'error': return 'error'
        case 'skipped': return 'warning'
        default: return 'info'
      }
    }

    const showSnackbar = (text, color = 'success') => {
      snackbarText.value = text
      snackbarColor.value = color
      snackbar.value = true
    }

    return {
      searchTerm,
      searching,
      searchPerformed,
      lastSearchTerm,
      searchResults,
      searchHeaders,
      importList,
      importListHeaders,
      importing,
      importResults,
      snackbar,
      snackbarText,
      snackbarColor,
      doSearch,
      isInImportList,
      addToImportList,
      removeFromImportList,
      clearImportList,
      doImport,
      resultAlertType
    }
  }
}
</script>
